import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { SuperHeader } from '@shared/components/SuperHeader/SuperHeader';
import { SuperSidebar } from '@shared/components/SuperSidebar/SuperSidebar';
import { SuperBottomSheet } from '@shared/components/SuperBottomSheet/SuperBottomSheet';
import { SuperIcon } from '@shared/components/SuperIcon/SuperIcon';
import { SuperAvatar } from '@shared/components/SuperAvatar/SuperAvatar';
import { useT } from '@shared/i18n/useT';
import { useAuthStore } from '@features/auth/store/auth.store';
import { useThemeStore } from '@app/theme/theme.store';
import { useIsMobile } from '@shared/hooks/useBreakpoint';
import type {
  SidebarSection,
  SidebarSectionItem,
} from '@shared/components/SuperSidebar/SuperSidebar.types';

function Brand() {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <img
        src="/greta-logo.png"
        alt="GretaBot"
        width={28}
        height={28}
        style={{ display: 'block', objectFit: 'contain' }}
      />
      <span
        style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 18,
          letterSpacing: '-0.01em',
          color: 'var(--color-text)',
        }}
      >
        Greta
        <span style={{ fontStyle: 'italic', color: 'var(--color-text-dim)' }}>Bot</span>
      </span>
    </div>
  );
}

export function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useT('common');
  const isMobile = useIsMobile();
  const user = useAuthStore((s) => s.user);
  const themeMode = useThemeStore((s) => s.mode);
  const toggleTheme = useThemeStore((s) => s.toggle);
  const [moreOpen, setMoreOpen] = useState(false);

  const sections: ReadonlyArray<SidebarSection> = [
    {
      title: t('navOperation'),
      items: [
        { id: 'dashboard', label: t('navDashboard'), icon: 'dashboard', to: '/dashboard' },
        { id: 'inbox', label: t('navInbox'), icon: 'inbox', to: '/inbox' },
        { id: 'customers', label: t('navCustomers'), icon: 'users', to: '/customers' },
        { id: 'ai-agents', label: t('navAiAgents'), icon: 'bot', to: '/ai-agents' },
        { id: 'knowledge', label: t('navKnowledge'), icon: 'book', to: '/knowledge' },
        { id: 'automations', label: t('navAutomations'), icon: 'zap', to: '/automations' },
        { id: 'tokens', label: t('navTokensCosts'), icon: 'coin', to: '/tokens-costs' },
        { id: 'reports', label: t('navReports'), icon: 'chart', to: '/reports' },
      ],
    },
    {
      title: t('navSystem'),
      items: [
        { id: 'integrations', label: t('navIntegrations'), icon: 'plug', to: '/integrations' },
        { id: 'settings', label: t('navSettings'), icon: 'settings', to: '/settings' },
      ],
    },
  ];

  const mobileShortcuts: ReadonlyArray<SidebarSectionItem> = [
    { id: 'dashboard', label: t('navDashboard'), icon: 'dashboard', to: '/dashboard' },
    { id: 'inbox', label: t('navInbox'), icon: 'inbox', to: '/inbox' },
    { id: 'customers', label: t('navCustomers'), icon: 'users', to: '/customers' },
    { id: 'integrations', label: t('navIntegrations'), icon: 'plug', to: '/integrations' },
  ];

  const allItems = sections.flatMap((s) => s.items);

  return (
    <div className="gb-ambient" style={{ minHeight: '100vh', background: 'var(--color-bg)' }}>
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          minHeight: '100vh',
          paddingBottom: isMobile ? 64 : 0,
        }}
      >
        {!isMobile && (
          <SuperSidebar
            sections={sections}
            activePath={location.pathname}
            brand={<Brand />}
            footer={
              user && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    paddingTop: 12,
                    borderTop: '1px solid var(--color-border-soft)',
                  }}
                >
                  <SuperAvatar name={user.name} size="sm" />
                  <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <span
                      style={{
                        fontSize: 12,
                        color: 'var(--color-text)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {user.name}
                    </span>
                    <span
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 9,
                        letterSpacing: '0.18em',
                        textTransform: 'uppercase',
                        color: 'var(--color-text-mute)',
                      }}
                    >
                      {user.role}
                    </span>
                  </span>
                </div>
              )
            }
          />
        )}

        <main style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <SuperHeader
            leading={isMobile ? <Brand /> : undefined}
            trailing={
              <>
                <button
                  type="button"
                  aria-label={
                    themeMode === 'dark' ? t('themeToggleToLight') : t('themeToggleToDark')
                  }
                  onClick={toggleTheme}
                  style={{
                    background: 'transparent',
                    border: '1px solid transparent',
                    borderRadius: 'var(--radius-md)',
                    width: 32,
                    height: 32,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--color-text-dim)',
                    cursor: 'pointer',
                  }}
                >
                  <SuperIcon name={themeMode === 'dark' ? 'sun' : 'moon'} size="md" />
                </button>
                <button
                  type="button"
                  aria-label="Notifications"
                  style={{
                    background: 'transparent',
                    border: '1px solid transparent',
                    borderRadius: 'var(--radius-md)',
                    width: 32,
                    height: 32,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--color-text-dim)',
                    cursor: 'pointer',
                  }}
                >
                  <SuperIcon name="bell" size="md" />
                </button>
                {user && <SuperAvatar name={user.name} size="sm" />}
              </>
            }
          />

          <div style={{ flex: 1, minWidth: 0 }}>
            <Outlet />
          </div>
        </main>

        {isMobile && (
          <SuperSidebar
            sections={sections}
            activePath={location.pathname}
            mobileShortcuts={mobileShortcuts}
            onMobileMore={() => setMoreOpen(true)}
          />
        )}
      </div>

      <SuperBottomSheet open={moreOpen} onClose={() => setMoreOpen(false)} title={t('navMore')}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {allItems.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                setMoreOpen(false);
                navigate(item.to);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: 12,
                background: 'transparent',
                border: 'none',
                color: 'var(--color-text)',
                cursor: 'pointer',
                textAlign: 'left',
                fontSize: 14,
              }}
            >
              <SuperIcon name={item.icon} size="md" />
              {item.label}
            </button>
          ))}
        </div>
      </SuperBottomSheet>
    </div>
  );
}
